import createAuth0Client from '@auth0/auth0-spa-js';
import * as usersController from './controllers/users';
import Vue from 'vue';
import { CommonLogger } from '../services/logger';

CommonLogger.init();

const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

let instance;

export const getInstance = () => instance;

// Creates an instance of the Auth0 SDK.
export const useAuth0 = ({
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  redirectUri = `${window.location.origin}/callback`,
  ...options
}) => {
  if (instance) return instance;

  instance = new Vue({
    data() {
      return {
        auth0Client: null,
        error: null,
        isAuthenticated: false,
        loading: true,
        popupOpen: false,
        user: {},
      };
    },
    methods: {
      loginWithRedirect(o) {
        return this.auth0Client.loginWithRedirect(o);
      },
      getIdTokenClaims(o) {
        return this.auth0Client.getIdTokenClaims(o);
      },
      getTokenSilently(o) {
        return this.auth0Client.getTokenSilently(o);
      },
      getTokenWithPopup(o) {
        return this.auth0Client.getTokenWithPopup(o);
      },
      logout(o) {
        return this.auth0Client.logout(o);
      },
      getExternalSSOAccount(accounts) {
        return accounts.find(({ user_id }) => !user_id.startsWith('auth0|'));
      },
      getAuth0Account(accounts) {
        return accounts.find(({ user_id }) => user_id.startsWith('auth0|'));
      },
      generateLogObject(error, success = false) {
        const { email, roles, sub } = this.user;

        return {
          data: {
            jsonPayload: {
              email,
              error,
              isAuthenticated: this.isAuthenticated,
              roles,
              sub,
              success,
            },
          },
        };
      },
      async mergeMatchedAccounts(user) {
        try {
          const isExternalSSOUser = !user.sub.startsWith('auth0|');

          if (isExternalSSOUser) {
            const token = await this.getTokenSilently();
            const userAccounts = await usersController.getUsersByEmail(user.email, token);

            if (userAccounts.length > 1) {
              const externalSSOAccount = this.getExternalSSOAccount(userAccounts);
              const auth0Account = this.getAuth0Account(userAccounts);

              await this.updateMetadata(externalSSOAccount, auth0Account, token);
              await this.updateRoles(externalSSOAccount, auth0Account, token);
              await this.linkUserMatchedIdentities(externalSSOAccount, auth0Account, token);

              await this.getTokenSilently({ ignoreCache: true });
              CommonLogger.info('MikMak Google User: Merging Matched Identities',
                this.generateLogObject(null, true));

              return;
            }
          }

          return;
        } catch (e) {
          CommonLogger.error('Issue with Google MikMak User in mergeMatchedAccounts()',
            this.generateLogObject(e.message));

          return;
        }
      },
      async updateMetadata(externalSSOAccount, auth0Account, token) {
        const userMetadata = auth0Account.user_metadata || {};
        const appMetadata = auth0Account.app_metadata || {};
        const externalSSOId = externalSSOAccount.user_id;

        await usersController.updateMetadataForSSOUser(
          externalSSOId,
          { appMetadata, userMetadata },
          token,
        );
      },
      async updateRoles(externalSSOAccount, auth0Account, token) {
        const externalSSOId = externalSSOAccount.user_id;
        const auth0Id = auth0Account.user_id;
        const userRoles = await usersController.getAuthUserRoles(auth0Id, token);

        if (userRoles.length > 0) {
          const roles = userRoles.map(({ id }) => id);

          await usersController.setAuthUserRoles(externalSSOId, { roles }, token);
        }
      },
      async linkUserMatchedIdentities(externalSSOAccount, auth0Account, token) {
        const externalSSOId = externalSSOAccount.user_id;
        const auth0Id = auth0Account.user_id;

        const secondaryIdentityData = {
          secondaryUserId: auth0Id,
        };

        await usersController.linkUserIdentities(externalSSOId, secondaryIdentityData, token);
      },
      async storeUserData(baseUser) {
        try {
          const { email, sub } = baseUser;
          const token = await this.getTokenSilently();
          const [
            [ auth0UserDetails ],
            userRoles,
          ] = await Promise.all([
            usersController.getUsersByEmail(email, token),
            usersController.getAuthUserRoles(sub, token),
          ]);

          if (!auth0UserDetails || !userRoles || !userRoles.length) {
            throw new Error(`No user data found for ${email}`);
          }

          const roles = userRoles.reduce((acc, roleObj) => ({
            ...acc,
            [roleObj.name]: true,
          }), {});
          const { created_at, user_metadata = {}, app_metadata = {} } = auth0UserDetails;
          const { brands = [], firebase_id = '' } = user_metadata;

          // check to see if user has 3.0 account
          const isAUserOfBothPlatforms = Boolean(app_metadata.swaven_user_id);

          const userDataToStore = {
            ...baseUser,
            isAUserOfBothPlatforms,
            auth_id: sub,
            brands,
            createdAt: created_at,
            id: firebase_id,
            roles,
            userMetadata: user_metadata,
          };

          this.user = userDataToStore;
          window.localStorage.setItem('mm-platform-user', JSON.stringify(userDataToStore));
        } catch (error) {
          CommonLogger.error('Unable to validate user role (auth)',
            this.generateLogObject(error.message));
        }
      },
    },
    // Use this lifecycle method to instantiate the SDK client
    async created() {
      try {
        this.auth0Client = await createAuth0Client({
          client_id: options.clientId,
          redirect_uri: redirectUri,
          audience: 'https://mikmak-oslo-auth-api/',
          cacheLocation: 'localstorage',
          domain: 'platform-login.mikmak.tv',
          scope: 'openid profile email',
          useRefreshTokens: true,
        });

        // If the user is returning to the app after authentication...
        if (
          window.location.search.includes('code=') &&
          window.location.search.includes('state=')
        ) {
          const { appState } = await this.auth0Client.handleRedirectCallback();

          this.user = await this.auth0Client.getUser() || {};

          await this.mergeMatchedAccounts(this.user);

          await this.storeUserData(this.user);

          this.error = null;

          // Notify subscribers that the redirect callback has happened, passing the appState
          //  (useful for retrieving any pre-authentication state)
          onRedirectCallback(appState);

          // If the user signs in but is denied access, logout
        } else if (window.location.search.includes('error=access_denied')) {
          this.logout();
          this.loginWithRedirect({ prompt: 'login' });
        } else {
          this.isAuthenticated = await this.auth0Client.isAuthenticated();

          if (this.isAuthenticated) {
            this.user = await this.auth0Client.getUser();
            await this.storeUserData(this.user);
          }
        }
      } catch (e) {
        this.error = e;

        CommonLogger.error(
          'Login attempt unable to connect',
          this.generateLogObject(e.message || 'Unexpected Error'),
        );
      } finally {
        // Initialize our internal authentication state
        this.isAuthenticated = await this.auth0Client.isAuthenticated();
        this.loading = false;
      }
    },
  });

  return instance;
};

export const Auth0Plugin = {
  install(Vue, options) {
    Vue.prototype.$auth = useAuth0(options);
  },
};
